import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthChrome } from '../components/auth/AuthChrome';
import {
  Button,
  ButtonRouterLink,
  Card,
  Spinner,
  TextField
} from '../components/primitives';
import { supabase } from '../lib/supabase';
import type { EmailOtpType } from '@supabase/supabase-js';
import './EmailConfirmation.css';

type View =
  | 'resolving'
  | 'success-signed-out'
  | 'success-signed-in'
  | 'already-confirmed'
  | 'expired'
  | 'used'
  | 'invalid'
  | 'mismatch';

type FailureReason = 'expired' | 'used' | 'invalid';

const RESOLVING_MIN_MS = 250;
const SUCCESS_REDIRECT_MS = 700;
const ALREADY_CONFIRMED_REDIRECT_MS = 800;
const RESEND_LIMIT_PER_HOUR = 5;
const RESEND_WINDOW_MS = 60 * 60 * 1000;

const VALID_EMAIL_TYPES: ReadonlySet<EmailOtpType> = new Set([
  'signup',
  'invite',
  'magiclink',
  'recovery',
  'email_change',
  'email'
]);

function parseEmailType(raw: string | null): EmailOtpType {
  if (raw && (VALID_EMAIL_TYPES as Set<string>).has(raw)) {
    return raw as EmailOtpType;
  }
  return 'email';
}

function classifyVerifyError(message: string | undefined): FailureReason {
  const msg = (message ?? '').toLowerCase();
  if (msg.includes('expired')) return 'expired';
  if (msg.includes('used') || msg.includes('already confirmed')) return 'used';
  return 'invalid';
}

export function EmailConfirmation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Supabase canonical param is `token_hash`; we accept `token` as a friendly
  // alias so the spec's example URLs work too.
  const tokenHash = searchParams.get('token_hash') ?? searchParams.get('token');
  const type = parseEmailType(searchParams.get('type'));

  const [view, setView] = useState<View>('resolving');
  const [tokenEmail, setTokenEmail] = useState<string>('');
  const [sessionEmail, setSessionEmail] = useState<string>('');

  // For mismatch resolution. We hold both sessions so the user's choice
  // ("Sign out and continue" vs "Stay signed in as me") can apply locally
  // without re-consuming the (already-spent) confirmation token.
  const newSessionTokens = useRef<{ access_token: string; refresh_token: string } | null>(null);
  const previousSessionTokens = useRef<{ access_token: string; refresh_token: string } | null>(
    null
  );

  // Resend (from the expired card). The button reveals an inline email input
  // because the consumed/expired token doesn't tell us which address to send to.
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendToast, setResendToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null
  );
  const resendAttempts = useRef<number[]>([]);

  const navigateAfter = useCallback(
    (ms: number, to = '/dashboard') => {
      const t = window.setTimeout(() => navigate(to, { replace: true }), ms);
      return () => window.clearTimeout(t);
    },
    [navigate]
  );

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    async function run() {
      // Missing token → invalid.
      if (!tokenHash) {
        await waitForResolvingFloor(startedAt);
        if (!cancelled) setView('invalid');
        return;
      }

      // Snapshot the existing session (if any) BEFORE verifying. Verify will
      // overwrite the session with the token's user, which we need to detect
      // mismatch and decide between "Stay signed in" / "Sign out and continue".
      const beforeRes = await supabase.auth.getSession();
      const before = beforeRes.data.session;
      previousSessionTokens.current = before
        ? { access_token: before.access_token, refresh_token: before.refresh_token }
        : null;
      const beforeEmail = before?.user.email ?? '';
      const beforeConfirmed = !!before?.user.email_confirmed_at;

      const verifyRes = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
      await waitForResolvingFloor(startedAt);
      if (cancelled) return;

      if (verifyRes.error || !verifyRes.data.session || !verifyRes.data.user) {
        // Failure path: classify message into expired / used / invalid.
        // Exception: if the existing user was already confirmed and matches
        // (or there's no way to know without an email), be friendly when
        // signed in with a confirmed email — render already-confirmed.
        if (before && beforeConfirmed) {
          setView('already-confirmed');
          navigateAfter(ALREADY_CONFIRMED_REDIRECT_MS);
          return;
        }
        setView(classifyVerifyError(verifyRes.error?.message) as View);
        return;
      }

      const newSession = verifyRes.data.session;
      const newEmail = verifyRes.data.user.email ?? '';
      newSessionTokens.current = {
        access_token: newSession.access_token,
        refresh_token: newSession.refresh_token
      };

      // Mismatch: signed in as someone else; the verify call just replaced
      // the session in storage. Restore the original session so the page
      // doesn't silently sign the user out of A while showing the mismatch
      // card. Their explicit choice decides which session wins.
      if (before && beforeEmail && beforeEmail.toLowerCase() !== newEmail.toLowerCase()) {
        await supabase.auth.setSession({
          access_token: before.access_token,
          refresh_token: before.refresh_token
        });
        if (cancelled) return;
        setTokenEmail(newEmail);
        setSessionEmail(beforeEmail);
        setView('mismatch');
        return;
      }

      // Signed in, matches, and was already confirmed: friendly success.
      if (before && beforeConfirmed) {
        setView('already-confirmed');
        navigateAfter(ALREADY_CONFIRMED_REDIRECT_MS);
        return;
      }

      // Signed in, matches, was unconfirmed: confirm + go.
      if (before) {
        setView('success-signed-in');
        navigateAfter(SUCCESS_REDIRECT_MS);
        return;
      }

      // Signed-out happy path: verify already signed us in.
      setView('success-signed-out');
      navigateAfter(SUCCESS_REDIRECT_MS);
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [tokenHash, type, navigateAfter]);

  async function handleMismatchSignOutContinue() {
    const tokens = newSessionTokens.current;
    if (!tokens) {
      navigate('/dashboard', { replace: true });
      return;
    }
    // Adopt the token's user session in place of the previous one.
    await supabase.auth.setSession(tokens);
    navigate('/dashboard', { replace: true });
  }

  async function handleResendSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (resendBusy) return;

    const trimmed = resendEmail.trim();
    if (!trimmed) {
      setResendToast({ kind: 'err', text: 'Enter your email to resend.' });
      return;
    }

    // Client-side rate-limit: 5 per hour, sliding window. Real product enforces
    // server-side too; UI reflects either with the same generic copy.
    const now = Date.now();
    resendAttempts.current = resendAttempts.current.filter((t) => now - t < RESEND_WINDOW_MS);
    if (resendAttempts.current.length >= RESEND_LIMIT_PER_HOUR) {
      setResendToast({ kind: 'err', text: 'Too many requests. Try again in a few minutes.' });
      return;
    }
    resendAttempts.current.push(now);

    setResendBusy(true);
    setResendToast(null);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: trimmed });
      if (error) {
        const msg = error.message?.toLowerCase() ?? '';
        if (msg.includes('rate') || msg.includes('too many')) {
          setResendToast({
            kind: 'err',
            text: 'Too many requests. Try again in a few minutes.'
          });
        } else {
          setResendToast({
            kind: 'err',
            text: "We couldn't send that email. Try again."
          });
        }
      } else {
        setResendToast({
          kind: 'ok',
          text: 'Confirmation email sent. Check your inbox.'
        });
      }
    } catch {
      setResendToast({
        kind: 'err',
        text: "We couldn't reach Penrose. Check your connection and try again."
      });
    } finally {
      setResendBusy(false);
    }
  }

  return (
    <>
      <AuthChrome pageLabel="Email confirmation" />
      <main className="ec-main">
        <Card className="ec-card" aria-live="polite" data-ec-view={view}>
          {view === 'resolving' ? (
            <div className="ec-resolving">
              <Spinner size="sm" label="Confirming your email" />
              <span className="ec-resolving-caption">Confirming your email…</span>
            </div>
          ) : null}

          {view === 'success-signed-out' ? (
            <>
              <h1 className="ec-heading">Email confirmed</h1>
              <p className="ec-lede">Signing you in and taking you to your dashboard…</p>
            </>
          ) : null}

          {view === 'success-signed-in' ? (
            <>
              <h1 className="ec-heading">Email confirmed</h1>
              <p className="ec-lede">Welcome back. Taking you to your dashboard…</p>
            </>
          ) : null}

          {view === 'already-confirmed' ? (
            <>
              <h1 className="ec-heading">Your email is already confirmed</h1>
              <p className="ec-lede">Taking you to your dashboard…</p>
            </>
          ) : null}

          {view === 'expired' ? (
            <>
              <h1 className="ec-heading">This confirmation link has expired</h1>
              <p className="ec-lede">Send a new one and we'll email you a fresh link.</p>
              {resendOpen ? (
                <form className="ec-resend-form" onSubmit={handleResendSubmit} noValidate>
                  <TextField
                    id="ec-resend-email"
                    label="Your email"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.currentTarget.value)}
                  />
                  <div className="ec-actions">
                    <Button type="submit" variant="primary" size="md" disabled={resendBusy}>
                      {resendBusy ? 'Sending…' : 'Send confirmation email'}
                    </Button>
                    <ButtonRouterLink to="/sign-in" variant="default" size="md">
                      Back to sign in
                    </ButtonRouterLink>
                  </div>
                </form>
              ) : (
                <div className="ec-actions">
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => setResendOpen(true)}
                  >
                    Resend confirmation
                  </Button>
                  <ButtonRouterLink to="/sign-in" variant="default" size="md">
                    Back to sign in
                  </ButtonRouterLink>
                </div>
              )}
              {resendToast ? (
                <div
                  className={`ec-toast ec-toast-${resendToast.kind}`}
                  role={resendToast.kind === 'err' ? 'alert' : 'status'}
                >
                  {resendToast.text}
                </div>
              ) : null}
            </>
          ) : null}

          {view === 'used' ? (
            <>
              <h1 className="ec-heading">This email is already confirmed</h1>
              <p className="ec-lede">
                The link can only be used once. Sign in to continue.
              </p>
              <div className="ec-actions">
                <ButtonRouterLink to="/sign-in" variant="primary" size="md">
                  Go to sign in
                </ButtonRouterLink>
              </div>
            </>
          ) : null}

          {view === 'invalid' ? (
            <>
              <h1 className="ec-heading">This link isn't valid</h1>
              <p className="ec-lede">
                The link doesn't match anything we recognize. Sign in or request a new
                confirmation email from your account.
              </p>
              <div className="ec-actions">
                <ButtonRouterLink to="/sign-in" variant="primary" size="md">
                  Go to sign in
                </ButtonRouterLink>
              </div>
            </>
          ) : null}

          {view === 'mismatch' ? (
            <>
              <h1 className="ec-heading">This link is for someone else</h1>
              <p className="ec-lede">
                It confirms <strong className="ec-emph">{tokenEmail}</strong>, but you're
                signed in as <strong className="ec-emph">{sessionEmail}</strong>. Sign out
                to confirm it.
              </p>
              <div className="ec-actions">
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={handleMismatchSignOutContinue}
                >
                  Sign out and continue
                </Button>
                <Link to="/dashboard" className="ec-link">
                  Stay signed in as me
                </Link>
              </div>
            </>
          ) : null}

        </Card>
      </main>
    </>
  );
}

async function waitForResolvingFloor(startedAt: number) {
  const elapsed = Date.now() - startedAt;
  if (elapsed >= RESOLVING_MIN_MS) return;
  await new Promise((r) => window.setTimeout(r, RESOLVING_MIN_MS - elapsed));
}
